import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface PublicSettings {
  site_name: string;
  site_description: string;
  default_cover: string | null;
  social_twitter: string | null;
  social_youtube: string | null;
  social_spotify: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private readonly endpoint = '/settings';
  private cachedSettings: PublicSettings | null = null;

  constructor(private apiService: ApiService) {}

  /**
   * Ottiene le impostazioni pubbliche (con cache)
   */
  getPublicSettings(): Observable<{ success: boolean; data: PublicSettings }> {
    // Se già in cache, restituisci dalla cache
    if (this.cachedSettings) {
      return of({ success: true, data: this.cachedSettings });
    }

    // Altrimenti fai la chiamata API
    return this.apiService.get<{ success: boolean; data: PublicSettings }>(
      `${this.endpoint}/public`
    ).pipe(
      tap(response => {
        // Salva in cache
        this.cachedSettings = response.data;
      }),
      catchError(error => {
        console.error('Error loading public settings:', error);
        // Restituisci valori di default in caso di errore
        return of({
          success: false,
          data: {
            site_name: 'PodLab',
            site_description: 'Il podcast su scienza e tecnologia',
            default_cover: null,
            social_twitter: null,
            social_youtube: null,
            social_spotify: null
          }
        });
      })
    );
  }

  /**
   * Ottiene solo la copertina di default
   */
  getDefaultCover(): Observable<string | null> {
    return new Observable(observer => {
      this.getPublicSettings().subscribe(response => {
        observer.next(response.data.default_cover);
        observer.complete();
      });
    });
  }

  /**
   * Pulisce la cache (utile per forzare il reload)
   */
  clearCache(): void {
    this.cachedSettings = null;
  }

  /**
   * Ottiene una singola impostazione per chiave (pubblica)
   */
  getByKey(key: string): Observable<{ success: boolean; data: { key: string; value: string } }> {
    return this.apiService.get<{ success: boolean; data: { key: string; value: string } }>(
      `${this.endpoint}/public/${key}`
    );
  }
}
